import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import {
  DataGridPremium,
  GridColDef,
  GridFeatureMode,
  GridRowModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-premium";
import FlexBetween from "../FlexBetween";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { getPremiumGridSx } from "../../styles/Theme";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import DataGridPaginationFooter from "./DataGridPaginationFooter";
import { useViewportTableHeight } from "../../hooks/useViewportTableHeight";

// System / action / selection columns that should never receive semantic
// (missing / diff / key) cell classes when variant="premium" is active.
const SKIP_FIELDS_PREMIUM = new Set([
  "action",
  "actions",
  "srNo",
  "id",
  "rowId",
  "__detail_panel_toggle__",
  "checkbox",
  "__check__",
  "__reorder__",
]);

interface DataTableProps {
  dataRows: any[];
  dataColumns: GridColDef[];
  loading?: boolean;
  checkboxSelection?: boolean;
  className?: string;
  buttonText?: string;
  buttonClass?: string;
  buttonClick?: () => void;
  buttonIcon?: React.ReactNode;
  dataListTitle?: string;
  disableColumnFilter?: boolean;
  rowCount?: number;
  pageSizeOptions?: number[];
  paginationModel?: any;
  onPaginationModelChange?: (params: any) => void;
  paginationMode?: GridFeatureMode;
  tableHeight?: number;
  singlePageMaxHeightDiff?: number;
  fitViewport?: boolean;
  disableColumnReorder?: boolean;
  columnGroupingModel?: any;
  setResultColumns?: any;
  pinnedColumns?: any;
  disableColumnPinning?: any;
  selectedIds?: any;
  setSelectedIds?: any;
  isExtraSpaceNeedToBeRemoved?: any;
  isExportButtonRequired?: boolean;
  // Phase 2A — opt-in premium styling for the Comparison Dashboard tables.
  // Adds the `.dashboard-premium-grid` class which the `premiumGridSx` block
  // targets, applies semantic cell classes (missing / key / diff) via
  // getCellClassName, and leaves all other grid instances visually untouched.
  variant?: "default" | "premium";
  // Phase 2B — resource prefixes used to derive the "base" suffix of each
  // resource-prefixed column. When two or more columns share a stripped
  // suffix and their row values differ, every involved cell is tagged
  // `cell-diff`. Only honored when variant="premium".
  resourcePrefixes?: string[];
}

// ── ReadMoreCell ─────────────────────────────────────────────────────────────
const ReadMoreCell: React.FC<{
  displayContent: React.ReactNode;
  rawValue: any;
  rowId: string | number;
  isExpanded: boolean;
  onToggle: (id: string | number, expanded: boolean) => void;
}> = ({ displayContent, rawValue, rowId, isExpanded, onToggle }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Measure overflow after each render while in collapsed state
  useLayoutEffect(() => {
    if (contentRef.current && !isExpanded) {
      const el = contentRef.current;
      const overflow = el.scrollHeight > el.clientHeight + 2;
      setHasOverflow((prev) => (prev !== overflow ? overflow : prev));
    }
  }, [isExpanded, rawValue]);

  const tooltipTitle =
    !isExpanded && hasOverflow
      ? typeof rawValue === "string"
        ? rawValue
        : ""
      : "";

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <Box sx={{ width: "100%", py: 0.5 }}>
        {/* Content box – clamped when collapsed */}
        <Box
          ref={contentRef}
          sx={
            !isExpanded
              ? {
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }
              : {
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }
          }
        >
          {displayContent ?? String(rawValue ?? "")}
        </Box>

        {/* Read More / Read Less toggle */}
        {hasOverflow && (
          <Typography
            variant="caption"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              display: "block",
              mt: 0.25,
              userSelect: "none",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(rowId, !isExpanded);
            }}
          >
            {isExpanded ? "Read Less ▲" : "Read More ▼"}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const RulesListDataTable: React.FC<DataTableProps> = ({
  dataRows,
  dataColumns,
  checkboxSelection = false,
  loading,
  className,
  buttonText,
  buttonClass,
  buttonClick,
  buttonIcon,
  dataListTitle,
  disableColumnFilter,
  rowCount,
  pageSizeOptions,
  paginationModel,
  onPaginationModelChange,
  paginationMode = "client",
  tableHeight,
  singlePageMaxHeightDiff,
  fitViewport = false,
  disableColumnReorder,
  columnGroupingModel,
  setResultColumns,
  pinnedColumns,
  disableColumnPinning,
  selectedIds,
  setSelectedIds,
  isExtraSpaceNeedToBeRemoved,
  isExportButtonRequired,
  variant = "default",
  resourcePrefixes,
}) => {
  const { containerRef, maxHeight: viewportMaxHeight } = useViewportTableHeight(
    fitViewport,
    singlePageMaxHeightDiff,
  );
  const isPremium = variant === "premium";
  // Dark-mode awareness: call getPremiumGridSx(mode) so frosted-glass headers,
  // semantic cell backgrounds (cell-missing, cell-diff, cell-key), border
  // colours, row hover tints, and footer surfaces all adapt automatically when
  // the user toggles theme (DQ-8674).
  const { mode } = useThemeMode();

  // ── Phase 2B — diff detection plumbing ────────────────────────────────────
  // Build (memoized): suffix → field[] for resource-prefixed columns. The
  // suffix is derived using the same stripping convention as
  // transformHeaderName in services/utils — prefix can appear at start,
  // middle, or end of the field name, separated by underscores.
  const suffixToFields = useMemo(() => {
    if (!isPremium || !resourcePrefixes || resourcePrefixes.length === 0) {
      return null;
    }
    const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const stripPrefix = (field: string): string | null => {
      let suffix = field;
      let matched = false;
      for (const prefix of resourcePrefixes) {
        const esc = escapeRegex(prefix);
        const next = suffix
          .replace(new RegExp(`_${esc}_`, "g"), "_")
          .replace(new RegExp(`^${esc}_`, "g"), "")
          .replace(new RegExp(`_${esc}$`, "g"), "")
          .replace(new RegExp(`^${esc}$`, "g"), "");
        if (next !== suffix) {
          matched = true;
          suffix = next;
        }
      }
      return matched && suffix.length > 0 ? suffix : null;
    };
    const map: Record<string, string[]> = {};
    dataColumns.forEach((col) => {
      if (!col?.field || SKIP_FIELDS_PREMIUM.has(col.field)) return;
      const suffix = stripPrefix(col.field);
      if (!suffix) return;
      if (!map[suffix]) map[suffix] = [];
      map[suffix].push(col.field);
    });
    // Only keep suffixes that actually have 2+ sibling columns — otherwise
    // there is nothing to diff against.
    Object.keys(map).forEach((k) => {
      if (map[k].length < 2) delete map[k];
    });
    return map;
  }, [isPremium, resourcePrefixes, dataColumns]);

  const fieldToSuffix = useMemo(() => {
    if (!suffixToFields) return null;
    const map: Record<string, string> = {};
    Object.entries(suffixToFields).forEach(([suffix, fields]) => {
      fields.forEach((f) => {
        map[f] = suffix;
      });
    });
    return map;
  }, [suffixToFields]);

  const isMissingValue = (value: any): boolean =>
    value === null ||
    value === undefined ||
    (typeof value === "string" &&
      (value.trim() === "" ||
        value.trim().toUpperCase() === "UNDEFINED" ||
        value.trim().toUpperCase() === "NULL"));

  // Semantic cell classification — only active under the premium variant so
  // we never alter the look of other DataGrids in the app.
  const getCellClassName = useCallback(
    (params: any) => {
      if (!isPremium) return "";
      const field = params?.field;
      // Skip system / action columns
      if (!field || SKIP_FIELDS_PREMIUM.has(field) || field.startsWith("__")) {
        return "";
      }
      const value = params?.value;
      if (isMissingValue(value)) return "cell-missing";

      // Diff detection — fire when this field has 2+ sibling resource columns
      // and at least two non-missing values among the siblings disagree.
      if (fieldToSuffix && suffixToFields) {
        const suffix = fieldToSuffix[field];
        if (suffix) {
          const siblings = suffixToFields[suffix];
          const present: any[] = [];
          for (const sf of siblings) {
            const v = params?.row?.[sf];
            if (!isMissingValue(v)) present.push(v);
          }
          if (present.length >= 2) {
            const first = present[0];
            const differs = present.some((v) => v !== first);
            if (differs) return "cell-diff";
          }
        }
      }

      // Heuristic: treat known key columns as anchors
      if (/^(rowId|id|.*[_-]?Key$|Key_.*)$/i.test(field)) return "cell-key";
      return "";
    },
    [isPremium, fieldToSuffix, suffixToFields],
  );

  const { rowsPerPageOptions } = useRowsPerPage();
  const dataGridProps = {
    // ... other props
    paginationMode,
    pagination: paginationMode !== "client",
    ...(paginationMode !== "client"
      ? {
          rowCount,
          pageSizeOptions: pageSizeOptions || rowsPerPageOptions,
          paginationModel,
          onPaginationModelChange,
        }
      : {
          pagination: true,
          pageSizeOptions:
            paginationMode === "client"
              ? pageSizeOptions || rowsPerPageOptions
              : undefined,
        }),
  };
  function CustomToolbar() {
    return (
      <GridToolbarContainer
        className={`custom-toolbar mt-16 ${
          isExportButtonRequired ? "alternative" : ""
        }`}
      >
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent={dataListTitle ? "space-between" : "flex-end"}
          sx={{ width: "100%", marginRight: 3, marginLeft: 3 }}
        >
          {dataListTitle && <h2 className="list-title">{dataListTitle}</h2>}
          {isExportButtonRequired && (
            <Box sx={{ display: "flex", columnGap: "10px" }}>
              <GridToolbarExport
                className="btn-orange btn-sm btn-border btn-export"
                excelOptions={{
                  fields: dataColumns
                    .filter(
                      (col) =>
                        col.field !== "action" &&
                        col.field !== "actions" &&
                        col.field !== "__detail_panel_toggle__" &&
                        col.field !== "checkbox" &&
                        col.field !== "srNo" &&
                        col.field !== "id",
                    )
                    .map((col) => col.field),
                }}
                printOptions={{ disableToolbarButton: true }}
                csvOptions={{ disableToolbarButton: true }}
              />
            </Box>
          )}
        </Stack>
      </GridToolbarContainer>
    );
  }
  const apiRef = useRef<GridApiPremium>(null!);

  // Handle horizontal scroll for movable headers
  React.useEffect(() => {
    const handleHorizontalScroll = () => {
      if (apiRef.current) {
        const virtualScroller =
          apiRef.current.rootElementRef?.current?.querySelector(
            ".MuiDataGrid-virtualScroller",
          );
        if (virtualScroller) {
          const scrollLeft = virtualScroller.scrollLeft;

          // Find movable GROUP HEADERS based on columnGroupingModel
          const getMovableGroupHeaders = () => {
            if (!columnGroupingModel || !Array.isArray(columnGroupingModel)) {
              return [];
            }

            const movableGroupHeaders: string[] = [];
            columnGroupingModel.forEach((group: any) => {
              if (
                group.movableClass &&
                group.movableClass.startsWith("movable-group-") &&
                group.children &&
                group.children.length > 5
              ) {
                if (group.headerName) {
                  movableGroupHeaders.push(group.headerName);
                }
              }
            });
            return movableGroupHeaders;
          };

          const movableGroupHeaders = getMovableGroupHeaders();

          // Target only GROUP HEADERS, not individual field headers
          const allHeaders = document.querySelectorAll(
            ".MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitle",
          );

          let appliedCount = 0;
          let foundFirst = false;

          // First pass: identify and mark GROUP HEADERS based on movable groups

          allHeaders.forEach((header) => {
            const headerText = header.textContent?.trim();

            const isMovableHeader = movableGroupHeaders.includes(
              headerText || "",
            );

            if (isMovableHeader && !foundFirst) {
              header.classList.add("onscroll-movable-header");
              foundFirst = true;
              appliedCount++;
            } else if (isMovableHeader) {
              header.classList.add("onscroll-movable-header");
              appliedCount++;
            } else {
              // Remove class from any other headers
              if (header.classList.contains("onscroll-movable-header")) {
                header.classList.remove("onscroll-movable-header");
              }
            }
          });

          // Second pass: calculate and apply individual offsets
          if (appliedCount > 0) {
            // Get width of first element that has onscroll-movable-header class
            const gwpHeaders = document.querySelectorAll(
              ".onscroll-movable-header",
            );
            const firstMovableWidth =
              gwpHeaders.length > 0 && gwpHeaders[0].parentElement
                ? (
                    gwpHeaders[0].parentElement as HTMLElement
                  ).getBoundingClientRect().width
                : 0;

            // Calculate total width of first 4 non-movable column groups
            const getNonMovableColumnsWidth = () => {
              const nonMovableGroups = [
                "Keys",
                "Resources",
                "Common Columns",
                "Messages",
              ];
              let totalWidth = 0;

              // Get all group headers (both movable and non-movable)
              const allGroupHeaders = document.querySelectorAll(
                ".MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitle",
              );

              nonMovableGroups.forEach((groupName) => {
                const groupHeader = Array.from(allGroupHeaders).find(
                  (header) => header.textContent?.trim() === groupName,
                );

                if (groupHeader && groupHeader.parentElement) {
                  const width = (
                    groupHeader.parentElement as HTMLElement
                  ).getBoundingClientRect().width;
                  totalWidth += width;
                }
              });

              return totalWidth;
            };

            const totalNonMovableWidth = getNonMovableColumnsWidth();

            let headerIndex = 0;
            allHeaders.forEach((header) => {
              if (header.classList.contains("onscroll-movable-header")) {
                let calculatedOffset = scrollLeft;

                // For headers after the first one, subtract the width of previous headers
                if (headerIndex > 0 && firstMovableWidth > 0) {
                  // Calculate offset for second and subsequent headers by subtracting width
                  // Example: scrollLeft = 100px, firstMovableWidth = 50px, headerIndex = 1
                  // Result: calculatedOffset = 100 - (50 * 1) = 50px
                  calculatedOffset = Math.max(
                    0,
                    scrollLeft - firstMovableWidth * headerIndex,
                  );
                }

                // Subtract the total width of non-movable columns from the final position
                const finalOffset = Math.max(
                  0,
                  calculatedOffset - totalNonMovableWidth / 2,
                );

                (header as HTMLElement).style.position = "relative";
                (header as HTMLElement).style.left = `${finalOffset}px`;
                (header as HTMLElement).style.zIndex = "1000";

                headerIndex++;
              }
            });
          }
        }
      }
    };

    const attachScrollListener = () => {
      if (apiRef.current) {
        const virtualScroller =
          apiRef.current.rootElementRef?.current?.querySelector(
            ".MuiDataGrid-virtualScroller",
          );
        if (virtualScroller) {
          virtualScroller.addEventListener("scroll", handleHorizontalScroll);
          return () => {
            virtualScroller.removeEventListener(
              "scroll",
              handleHorizontalScroll,
            );
          };
        }
      }
      return undefined;
    };

    // Apply initial styles and attach listener
    const timeoutId = setTimeout(() => {
      // Get movable GROUP HEADERS from columnGroupingModel
      const getMovableGroupHeaders = () => {
        if (!columnGroupingModel || !Array.isArray(columnGroupingModel)) {
          return [];
        }

        const movableGroupHeaders: string[] = [];
        columnGroupingModel.forEach((group: any) => {
          if (
            group.movableClass &&
            group.movableClass.startsWith("movable-group-") &&
            group.children &&
            group.children.length > 5
          ) {
            if (group.headerName) {
              movableGroupHeaders.push(group.headerName);
            }
          }
        });
        return movableGroupHeaders;
      };

      const movableGroupHeaders = getMovableGroupHeaders();
      // Target only GROUP HEADERS, not individual field headers
      const allHeaders = document.querySelectorAll(
        ".MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitle",
      );

      let initialCount = 0;
      let foundFirst = false;

      // First pass: identify and mark GROUP HEADERS based on movable groups
      allHeaders.forEach((header) => {
        const headerText = header.textContent?.trim();
        const isMovableHeader = movableGroupHeaders.includes(headerText || "");

        if (isMovableHeader && !foundFirst) {
          header.classList.add("onscroll-movable-header");
          foundFirst = true;
          initialCount++;
        } else if (isMovableHeader) {
          header.classList.add("onscroll-movable-header");
          initialCount++;
        } else {
          // Remove class from any other headers
          if (header.classList.contains("onscroll-movable-header")) {
            header.classList.remove("onscroll-movable-header");
          }
        }
      });

      // Second pass: apply initial positioning (offset = 0)
      if (initialCount > 0) {
        allHeaders.forEach((header) => {
          if (header.classList.contains("onscroll-movable-header")) {
            let calculatedOffset = 0; // Initial offset is 0

            (header as HTMLElement).style.position = "relative";
            (header as HTMLElement).style.left = `${calculatedOffset}px`;
            (header as HTMLElement).style.zIndex = "1000";
          }
        });
      }

      attachScrollListener();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [columnGroupingModel]);

  React.useEffect(() => {
    if (!setResultColumns) return;
    if (selectedIds) {
      const selectedRows = dataRows.filter((row) => {
        return selectedIds.includes(row.rowId);
      });
      setResultColumns((prev: any) => {
        const updatedColumns =
          selectedIds && selectedIds.length === 0 ? [] : [...prev];
        selectedRows.forEach((row) => {
          const formattedRow = Object.keys(row).reduce((acc: any, key: any) => {
            if (key !== "message" || key !== "Quantity") {
              const newKey = key.replace(/'/g, "");
              acc[newKey] = row[key];
            }
            return acc;
          }, {});
          if (
            !updatedColumns.some((item) => item.rowId === formattedRow.rowId)
          ) {
            updatedColumns.push(formattedRow);
          }
        });
        return updatedColumns;
      });
    }
  }, [selectedIds]);

  const [maxHeight, setMaxHeight] = useState<any>(
    window.innerHeight - (singlePageMaxHeightDiff ?? 0),
  );
  React.useEffect(() => {
    const handleResize = () => {
      setMaxHeight(window.innerHeight - (singlePageMaxHeightDiff ?? 0));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    if (apiRef.current) {
      setTimeout(() => {
        apiRef?.current?.resetRowHeights && apiRef?.current?.resetRowHeights();
      }, 300);
    }
  }, [dataRows]);
  const previousSelectedIds = useRef(selectedIds);
  useEffect(() => {
    // Before data reload, persist the selectedIds
    previousSelectedIds.current = selectedIds;
  }, [selectedIds]);
  useEffect(() => {
    if (dataRows && dataRows.length && setSelectedIds) {
      setSelectedIds(previousSelectedIds.current); // Restore selectedIds after data reload
    }
  }, [dataRows]);
  // ── Expanded rows state (Read More / Read Less) ───────────────────────────
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set(),
  );

  const handleToggleExpand = useCallback(
    (id: string | number, expanded: boolean) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (expanded) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [],
  );

  // Recalculate row heights whenever expanded state changes
  useEffect(() => {
    setTimeout(() => {
      apiRef.current?.resetRowHeights?.();
    }, 0);
  }, [expandedRows]);

  // Columns enhanced with ReadMoreCell (skip action / id / boolean cols)
  const SKIP_FIELDS = new Set([
    "action",
    "actions",
    "srNo",
    "id",
    "__detail_panel_toggle__",
    "checkbox",
  ]);

  const processedColumns = useMemo(() => {
    return dataColumns.map((col) => {
      if (SKIP_FIELDS.has(col.field) || col.type === "boolean") return col;
      return {
        ...col,
        renderCell: (params: any) => {
          const rowId = params.row?.rowId ?? params.row?.id ?? params.id;
          const isExpanded = expandedRows.has(rowId);
          const displayContent = col.renderCell
            ? col.renderCell(params)
            : params.value;
          return (
            <ReadMoreCell
              displayContent={displayContent}
              rawValue={params.value}
              rowId={rowId}
              isExpanded={isExpanded}
              onToggle={handleToggleExpand}
            />
          );
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataColumns, expandedRows, handleToggleExpand]);
  // ─────────────────────────────────────────────────────────────────────────

  const CustomFooter = ({ selectedIds }: any) => (
    <div className="datatable-footer">
      <strong>Selected IDs:</strong> [{selectedIds.join(", ")}]
      <Button
        className="btn-orange btn-dark btn-sm plus-btn-sm"
        onClick={() => setSelectedIds([])}
      >
        Reset
      </Button>
    </div>
  );

  return (
    <FlexBetween height="100%" width="100%">
      <div
        ref={containerRef}
        style={{
          height: pinnedColumns
            ? `${
                dataRows.length < 5
                  ? dataRows.length * 60 + 215
                  : dataRows.length < 11
                    ? dataRows.length * 40 + 100
                    : maxHeight + 95
              }px`
            : "auto",
          width: "100%",
        }}
      >
        <DataGridPremium
          apiRef={apiRef}
          getRowHeight={(params) =>
            expandedRows.has(params.id) ? "auto" : null
          }
          className={`${className}
          ${columnGroupingModel ? "columnGroupingModel" : ""}
          ${setResultColumns ? "checkbox-result-column" : ""}
          ${isPremium ? "dashboard-premium-grid" : ""} `}
          getCellClassName={getCellClassName}
          sx={{
            ...(isExtraSpaceNeedToBeRemoved && {
              "&.dataTable": {
                height: "auto !important",
                maxHeight: "100% !important",
              },
            }),
            "&.columnGroupingModel": {
              "& .MuiDataGrid-columnHeaders": {
                overflow: "visible",
              },
              "& .MuiDataGrid-row": {
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
            },
            "& .MuiDataGrid-main": {
              position: "relative",
              "& .MuiDataGrid-columnHeader--filledGroup": {
                borderBottom: "solid 1px #e0e0e0",
                "& .MuiDataGrid-withBorderColor": {
                  borderColor: "transparent",
                },
                // "& + .MuiDataGrid-columnHeader--filledGroup ": {
                //   "&:before": {
                //     content: '""',
                //     background: "#e0e0e0",
                //     width: "1px",
                //     height: "1000px",
                //     position: "absolute",
                //     top: "0px",
                //     left: "0px",
                //     zIndex: "9999999",
                //   },
                // },
              },
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#146FD0",
              fontWeight: 700,
            },
            "& .MuiDataGrid-overlayWrapperInner": {
              // height: "auto !important", //commented because its resizing the overlay window backdrop widow

              "& .MuiDataGrid-overlay": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
              },
            },
            "& .MuiDataGrid-footerContainer": {},
            minHeight: "100px",
            // disable sort arrow in header
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {},
            "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainerContent .MuiCheckbox-root":
              { display: "none" },
            "& .MuiDataGrid-menuIcon": {
              display: "block",
            },
            "& .MuiDataGrid-sortIcon": {},
            ...(isPremium ? getPremiumGridSx(mode) : {}),
            "& .MuiDataGrid-virtualScroller": {
              minHeight: singlePageMaxHeightDiff
                ? "100px"
                : columnGroupingModel
                  ? "inherit"
                  : "100px",
              maxHeight: viewportMaxHeight ?? (
                singlePageMaxHeightDiff
                  ? `calc(100vh - ${singlePageMaxHeightDiff}px)`
                  : tableHeight && dataRows.length < 6
                    ? tableHeight
                    : `calc(100vh - 230px)`
              ),
              overflowY: "auto !important",
              // overflowX: { md: "auto" },
            },
          }}
          rows={dataRows}
          columns={processedColumns}
          checkboxSelection={checkboxSelection}
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(selectionModel) => {
            if (!setResultColumns) return;
            if (selectedIds) {
              const currentSelection = selectionModel;
              if (currentSelection.length > selectedIds.length) {
                setSelectedIds((prevSelectedIds: any) => {
                  const updatedSelectedIds = new Set(prevSelectedIds);
                  selectionModel.forEach((id: any) => {
                    return updatedSelectedIds.add(id);
                  });
                  return Array.from(updatedSelectedIds);
                });
              } else {
                const deselectedId = selectedIds.find(
                  (id: any) => !currentSelection.includes(id),
                );
                setSelectedIds((prev: any) => {
                  return prev.filter((item: any) => item !== deselectedId);
                });
              }
            }
          }}
          loading={loading}
          getRowId={(row: GridRowModel) => (row?.rowId ? row?.rowId : row?.id)}
          //autoHeight={true}
          disableColumnFilter={disableColumnFilter}
          disableColumnSelector={true}
          disableColumnMenu={false}
          getRowClassName={(params: any) => {
            return params?.row?.is_derived ? "derived-column" : "";
          }}
          disableRowGrouping={true}
          disableAggregation={true}
          slots={{
            ...((dataListTitle || isExportButtonRequired) && {
              toolbar: CustomToolbar,
            }),
            noRowsOverlay: CustomNoRowsOverlay,
            footer: () => (
              <DataGridPaginationFooter pageSizeOptions={pageSizeOptions}>
                {selectedIds &&
                selectedIds.length > 0 &&
                checkboxSelection ? (
                  <CustomFooter selectedIds={selectedIds} />
                ) : (
                  <div />
                )}
              </DataGridPaginationFooter>
            ),
          }}
          //for server side pagination
          {...dataGridProps}
          disableColumnReorder={disableColumnReorder ? true : false}
          columnGroupingModel={columnGroupingModel ? columnGroupingModel : []}
          experimentalFeatures={{
            columnGrouping: columnGroupingModel ? true : false,
          }}
          pinnedColumns={pinnedColumns}
          disableColumnPinning={
            disableColumnPinning ? true : disableColumnPinning
          }
        />
      </div>
    </FlexBetween>
  );
};

export default RulesListDataTable;
